import {
  ApiProject,
  ChecklistDocumentItem,
  ChecklistItem,
  ChecklistQuestionItem,
  BudgetRow,
  ComparisonRow,
  DocumentRow,
  PreconFormValues,
  YesNoItem,
  YesNoValue,
} from "../types/precon.types";

export interface NormalizeResult {
  initialValues: PreconFormValues;
  prefilledFields: Set<keyof PreconFormValues>;
}

type ChecklistMap = Record<string, ChecklistItem>;

function buildChecklistMap(checklist: ChecklistItem[] = []): ChecklistMap {
  return checklist.reduce<ChecklistMap>((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {});
}

// Extract a simple string value from a question checklist item
function getQString(map: ChecklistMap, key: string): string {
  const item = map[key];
  if (!item || item.type !== "question") return "";
  const val = (item as ChecklistQuestionItem).value;
  if (val === null || val === undefined) return "";
  if (typeof val === "object") return "";
  return String(val);
}

// Extract a sub-key from an object-valued question (e.g. square_footage.total_sqft)
function getQObjectField(
  map: ChecklistMap,
  key: string,
  subKey: string,
): string {
  const item = map[key];
  if (!item || item.type !== "question") return "";
  const val = (item as ChecklistQuestionItem).value;
  if (typeof val !== "object" || val === null) return "";
  return String((val as Record<string, string>)[subKey] ?? "");
}

// Returns raw checklist question value (any type) or undefined if key not in map
function getQRaw(map: ChecklistMap, key: string): unknown {
  const item = map[key];
  if (!item || item.type !== "question") return undefined;
  return (item as ChecklistQuestionItem).value;
}

// non-null value → "yes", null/missing → "no"
function toYesNo(val: unknown): YesNoValue {
  if (val == null) return "no";

  if (typeof val === "string") {
    const v = val.trim().toLowerCase();

    if (v === "yes") return "yes";
    if (v === "no") return "no";
    if (v === "n/a") return "na";
  }

  if (typeof val === "boolean") {
    return val ? "yes" : "no";
  }

  return "yes";
}

const TODAY = new Date().toISOString().slice(0, 10);

const DEFAULT_COMPARISON_ROWS: ComparisonRow[] = [];

export const DEFAULT_YES_NO_ITEMS: YesNoItem[] = [
  {
    id: "bond",
    label: "Performance & Payment Bond",
    subLabel: "Required by bank or contract",
    value: "no",
  },
  { id: "liquidated", label: "Liquidated Damages", value: "no" },
  { id: "bonus", label: "Early Completion Bonus", value: "no" },
  { id: "savings", label: "Shared Savings Clause", value: "no" },
  {
    id: "permits",
    label: "Permits by Owner/GC",
    subLabel: "Building permits prior to first draw",
    value: "yes",
  },
  {
    id: "buildersRisk",
    label: "Builders Risk Insurance by Owner/GC",
    value: "yes",
  },
  {
    id: "liability",
    label: "General Liability & Workers Comp by Owner/GC",
    value: "yes",
  },
];

export const BUDGET_TOTAL_ROW: BudgetRow = {
  id: "total",
  trade: "Totals",
  value: "—",
  projectSf: "—",
  projectPsf: "",
  industryLow: "",
  industryHigh: "",
  notes: "",
  isTotal: true,
};

export function normalizePreconReport(project: ApiProject): NormalizeResult {
  const prefilled = new Set<keyof PreconFormValues>();
  const checklistMap = buildChecklistMap(project.checklist);
  function fill<K extends keyof PreconFormValues>(
    key: K,
    value: string,
  ): string {
    if (value) prefilled.add(key);
    return value;
  }

  const fmtMoney = (n?: number) =>
    n != null && n > 0 ? `$${n.toLocaleString()}` : "";
  const fmtPct = (n?: number) => (n != null && n > 0 ? `${n}%` : "");

  // ── Step 1: Project Setup ────────────────────────────────────────────────
  const bank = fill("bank", project.lender?.companyName ?? "");

  const officerRaw = project.lendingOfficer;
  const officerName = officerRaw
    ? `${officerRaw.firstName ?? ""} ${officerRaw.lastName ?? ""}`.trim()
    : "";
  const lendingOfficer = fill("lendingOfficer", officerName);

  const invoiceNumber = fill("invoiceNumber", project.invoiceNumber ?? "");
  const preconReportFeeAmount =
    project.projectSettings?.preConstructionReportFee?.amount;
  const invoiceAmount = fill(
    "invoiceAmount",
    preconReportFeeAmount != null ? String(preconReportFeeAmount) : "",
  );

  const projectName = fill("projectName", project.name ?? "");
  const projectDescription = fill(
    "projectDescription",
    project.description ?? "",
  );
  const projectType = fill("projectType", (project.type ?? "").toLowerCase());
  const projectAddress = fill("projectAddress", project.address ?? "");

  const buildPersonLabel = (p?: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
  }) => {
    if (!p) return "";
    const fullName = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
    return [p.companyName, fullName].filter(Boolean).join(" | ");
  };

  const ownerName = fill("ownerName", buildPersonLabel(project.owner));
  const gcName = fill("gcName", buildPersonLabel(project.gc));
  const architectName = fill(
    "architectName",
    buildPersonLabel(project.architect),
  );

  // ── Documents checklist → split into two columns ─────────────────────────
  const fmtDocLabel = (key: string) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const documentItems = (project.checklist ?? []).filter(
    (item): item is ChecklistDocumentItem => item.type === "document",
  );
  const allDocRows: DocumentRow[] = documentItems.map((item) => ({
    id: item.key,
    label: fmtDocLabel(item.key),
    status: item.value?.key ? "done" : "na",
    rawValue: item.value ?? null,
  }));
  const mid = Math.ceil(allDocRows.length / 2);
  const docsLeft = allDocRows.slice(0, mid);
  const docsRight = allDocRows.slice(mid);

  // ── Step 3: Executive Summary ─────────────────────────────────────────────
  const primaryDescriptionPoints = fill(
    "primaryDescriptionPoints",
    getQString(checklistMap, "project_primary_description"),
  );

  // square_footage value can be a plain string ("1200") or an object ({total_sqft:"24000",...})
  const sqFtStr = getQString(checklistMap, "square_footage");
  const sqFtObj = getQObjectField(checklistMap, "square_footage", "total_sqft");
  const sqFtRaw = sqFtStr || sqFtObj;
  const totalSf = fill("totalSf", sqFtRaw ? `${sqFtRaw} SF` : "");
  const totalCostRaw = getQObjectField(checklistMap, "total_cost", "amount");
  const totalBudget = fill(
    "totalBudget",
    fmtMoney(
      totalCostRaw ? parseFloat(totalCostRaw) : project.contractValue?.amount,
    ),
  );
  const hardCostBudget = fill(
    "hardCostBudget",
    getQObjectField(checklistMap, "hard_cost", "amount"),
  );
  const mobilizationDate = fill(
    "mobilizationDate",
    getQString(checklistMap, "mobilization_date"),
  );
  const estCompletion = fill(
    "estCompletion",
    getQString(checklistMap, "estimated_date"),
  );

  // ── Step 4: Contract Delivery ─────────────────────────────────────────────
  const contractorFee = fill(
    "contractorFee",
    getQString(checklistMap, "contractor_fees"),
  );
  const retainageRateRaw = project.projectSettings?.retainageRate;
  const retainage = fill(
    "retainage",
    retainageRateRaw != null
      ? `${parseFloat(String(retainageRateRaw))}%`
      : getQString(checklistMap, "gc_retainage") || "0%",
  );

  // Contingency: handle both { gcContingencyAmount } and { contingency.gcContingency } shapes
  const gcContingencyRaw =
    project.gcContingencyAmount?.amount?.amount ??
    project.contingency?.gcContingency?.sovTotal;
  const checklistContingencyRaw = getQObjectField(
    checklistMap,
    "contingency_amount",
    "amount",
  );
  const contingencyAmountNum = gcContingencyRaw
    ? gcContingencyRaw
    : checklistContingencyRaw
      ? parseFloat(checklistContingencyRaw)
      : undefined;
  const contingencyDisplay = fill(
    "contingencyDisplay",
    fmtMoney(contingencyAmountNum),
  );
  const gcContingencyAmount = fill(
    "gcContingencyAmount",
    fmtMoney(contingencyAmountNum),
  );
  const contingencyPct = fill(
    "contingencyPct",
    fmtPct(project.gcContingencyAmount?.percentage),
  );

  // ── YesNo items from checklist Q&A ───────────────────────────────────────
  const bondRaw = getQRaw(checklistMap, "is_bond_required");
  const sharedSavingRaw = getQRaw(checklistMap, "shared_saving");
  const damagesOrBonusRaw = getQRaw(
    checklistMap,
    "damages_or_early_completion_bonus",
  );

  const permitsRaw = getQRaw(checklistMap, "build_permit_payer");
  const buildersRiskRaw = getQRaw(
    checklistMap,
    "builder_risk_insurance_provider",
  );

  const yesNoItems: YesNoItem[] = DEFAULT_YES_NO_ITEMS.map((item) => {
    if (item.id === "bond")
      return bondRaw !== undefined
        ? { ...item, value: toYesNo(bondRaw) }
        : item;
    if (item.id === "liquidated")
      return damagesOrBonusRaw !== undefined
        ? { ...item, value: toYesNo(damagesOrBonusRaw) }
        : item;
    if (item.id === "bonus")
      return damagesOrBonusRaw !== undefined
        ? { ...item, value: toYesNo(damagesOrBonusRaw) }
        : item;
    if (item.id === "savings")
      return sharedSavingRaw !== undefined
        ? { ...item, value: toYesNo(sharedSavingRaw) }
        : item;
    if (item.id === "permits")
      return permitsRaw !== undefined
        ? { ...item, value: toYesNo(permitsRaw) }
        : item;
    if (item.id === "buildersRisk")
      return buildersRiskRaw !== undefined
        ? { ...item, value: toYesNo(buildersRiskRaw) }
        : item;
    return item;
  });

  // ── Step 6: Lien items → budget rows ─────────────────────────────────────
  const lienItems = project.lienItems ?? [];
  const budgetRows: BudgetRow[] =
    lienItems.length > 0
      ? [
          ...lienItems.map((item, i) => ({
            id: `row-${item.displayOrder ?? i}`,
            trade: item.description ?? "",
            value: fmtMoney(item.amount?.amount),
            projectSf: "",
            projectPsf: "",
            industryLow: "",
            industryHigh: "",
            notes: "",
          })),
          BUDGET_TOTAL_ROW,
        ]
      : [BUDGET_TOTAL_ROW];

  const initialValues: PreconFormValues = {
    // Step 1
    bank,
    lendingOfficer,
    projectName,
    projectDescription,
    projectType,
    ownerName,
    gcName,
    architectName,
    servicesRequested: "Scope of Work, Plan & Cost Review",
    reportDate: TODAY,
    invoiceNumber,
    invoiceAmount,
    preparedBy: "",
    projectAddress,
    reviewDate: TODAY,
    docsLeft,
    docsRight,

    // Step 2
    certificationOpening: "",
    certificationStandards: "",
    certificationFindings: "",
    certificationPreparedBy: "",
    certificationOrganization: "",
    certificationPhone: "",
    certificationEmail: "",

    // Step 3
    primaryDescriptionPoints,
    totalSf,
    totalUnitsPhase1: "",
    totalUnitsFull: "",
    additionalDescription: "",
    totalBudget,
    hardCostBudget,
    costPerSf: "",
    mobilizationDate,
    estCompletion,

    // Step 4
    contractType: "",
    contractorFee,
    contingencyDisplay,
    retainage,
    yesNoItems,
    contractNarrative: "",

    // Step 5
    allowancesNarrative: "",
    gcContingencyAmount,
    contingencyPct,
    industryRange: "5.0% – 10.0%",
    contingencyNarrative: "",
    clarificationsNarrative: "",

    // Step 6
    budgetRows,
    comparisonRows: DEFAULT_COMPARISON_ROWS,
    budgetNarrative: "",
    budgetConclusion: "",
    borrowerEquityRequired:
      project.projectSettings?.borrowerEquityRequired ?? false,
    borrowerEquityAmount: fill(
      "borrowerEquityAmount",
      fmtMoney(project.projectSettings?.borrowerEquityDetails?.equity?.amount),
    ),
    borrowerEquityDescription: fill(
      "borrowerEquityDescription",
      project.projectSettings?.borrowerEquityDetails?.description?.trim() ?? "",
    ),

    // Step 7
    masterScheduleProvided: "No — not provided",
    typicalDuration: "12–16 months",
    statedDuration: "",
    scheduleNarrative: "",
    closingNarrative: "",
    actionItems: [],
    documentsReviewed: "",
    preconRequirements: "",
    drawRequirements: "",
    closeoutRequirements: "",

    // Step 8
    deliverTo: fill(
      "deliverTo",
      officerName && bank
        ? `${officerName} (${bank})`
        : officerName || bank || "",
    ),
    deliveryCC: [],
    deliveryMethod: "email_platform",
    lockAfterPublish: "yes",
  };

  return { initialValues, prefilledFields: prefilled };
}

export function emptyInitialValues(): PreconFormValues {
  return normalizePreconReport({ id: "" }).initialValues;
}
